import { Button, Flex, Heading, List, Text, View } from "@instructure/ui";
import { t } from "i18next";
import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import RichTextEditor from "react-rte";
import EmbeddedDashboardResults from "../../components/Assessment/APDashboard/Results/Displays/EmbeddedDashboardResults";
import FilterTray from "../../components/Assessment/APDashboard/Results/FilterTray";
import ResultsSkeleton from "../../components/Assessment/APDashboard/Results/ResultsSkeleton";
import {
  CANVAS,
  EMBEDDED,
  EXTERNAL,
  QUALITATIVE,
} from "../../components/Assessment/Plan/utils";
import { Card } from "../../components/Card";
import {
  ERROR,
  useNotification,
} from "../../components/CustomHooks/NotificationProvider";
import PublicTextArea from "../../components/CustomTextArea/PublicTextArea";
import { ExternalService } from "../../services/ExternalService";
import { Files } from "../../services/FilesService";
import PublicAssessmentChart from "./PublicAssessmentChart";
import PublicCanvasJuriedAssessmentChart from "./PublicCanvasJuriedAssessmentChart";
import PublicExternalJuriedAssessmentChart from "./PublicExternalJuriedAssessmentChart";
import PublicFlexibleAssessmentChart from "./PublicFlexibleAssessmentChart";
import PublicQualitativeMeasure from "./PublicQualitativeMeasure";

const H2_THEME_OVERRIDE = { h2FontWeight: 700 };

const PublicResults = () => {
  const showNotification = useNotification();
  const { tenantId, shareId } = useParams();
  const topRef = useRef();
  const bottomRef = useRef();

  const [publicResults, setPublicResults] = useState(null);
  const [mission, setMission] = useState("");
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [canvasView, setCanvasView] = useState("view1");
  const [jaView, setJaView] = useState("view1");
  const [filters, setFilters] = useState({
    mode: "term",
    masteryResults: "true",
  });
  const [prevFilters, setPrevFilters] = useState({
    mode: "term",
    masteryResults: "true",
  });
  const [triggerMap, setTriggerMap] = useState(true);

  useEffect(() => {
    const loadPublicData = async () => {
      try {
        setLoading(true);
        const publicShareCode = await ExternalService.getPublicShareCode(
          shareId,
          tenantId
        );
        const resultsPromise = ExternalService.getPublicResultsByPlanId(
          publicShareCode.payload.context.plan_id,
          tenantId
        );
        const missionPromise = Files.getPublicFileContent(
          "response.html",
          "public-mission",
          publicShareCode.payload.context.plan_id,
          tenantId
        );

        const [results, mission] = await Promise.all([
          resultsPromise,
          missionPromise,
        ]);

        if (results) {
          results.data = results.data.sort((a, b) => a.sequence - b.sequence);
          results.data.forEach((section) => {
            section.assessments.forEach((measure) => {
              measure.type =
                measure.assessment_type === EXTERNAL ? EXTERNAL : CANVAS;
            });
            section.qualitative_measures.forEach((measure) => {
              measure.type = QUALITATIVE;
            });
            section.embedded_datalinks.forEach((measure) => {
              measure.type = EMBEDDED;
            });
            section.measures = section.assessments
              .concat(section.qualitative_measures)
              .concat(section.embedded_datalinks)
              .sort((a, b) => a.sequence - b.sequence);
          });

          setPublicResults(results);
          setSections(results.data);
        } else {
          setPublicResults({});
        }
        setMission(mission.content);
        setLoading(false);
      } catch {
        showNotification(
          t("notifications.general.fetchError", { obj: t("results.results") }),
          ERROR
        );
      }
    };
    loadPublicData();
  }, [shareId, tenantId, showNotification]);

  // canvasView:
  // view1: mastery results, term view
  // view2: mastery results, course view
  // view3: grade results, term view
  // view4: grade results, course view
  //
  // jaView:
  // view1: mastery results
  // view2: grade results
  useEffect(() => {
    if (triggerMap) {
      if (filters.masteryResults === "true") {
        setJaView("view1");
        if (filters.mode === "term") {
          setCanvasView("view1");
        } else {
          setCanvasView("view3");
        }
      } else {
        setJaView("view2");
        if (filters.mode === "term") {
          setCanvasView("view2");
        } else {
          setCanvasView("view4");
        }
      }
      setTriggerMap(false);
    }
  }, [triggerMap]);

  return !loading ? (
    <View as="div" background="primary" position="relative">
      <span ref={topRef} />
      <Heading level="h2" as="h1" border="bottom" margin="0 0 small 0">
        {t("results.forCycle")} {publicResults?.cycle_name}
      </Heading>
      <Flex gap="small">
        <Flex.Item>
          <FilterTray
            filters={filters}
            setFilters={setFilters}
            prevFilters={prevFilters}
            setPrevFilters={setPrevFilters}
            setTriggerMap={setTriggerMap}
          />
        </Flex.Item>
        <Flex.Item>
          <Button
            size="small"
            onClick={() => bottomRef.current.scrollIntoView()}
          >
            {t("results.planFindingsAndRecommendations")}
          </Button>
        </Flex.Item>
      </Flex>
      <Flex gap="large" wrap="wrap" margin="small 0 0 0">
        {mission !== "" && (
          <Flex.Item size="100%">
            <Heading
              level="h2"
              border="bottom"
              themeOverride={H2_THEME_OVERRIDE}
            >
              {t("assessment.mission")}:
            </Heading>

            <View display="block" margin="small medium 0 large">
              <RichTextEditor
                className="readOnlyClass"
                readOnly
                value={RichTextEditor.createValueFromString(mission, "html")}
              />
            </View>
          </Flex.Item>
        )}
        {publicResults.goals && (
          <Flex.Item size="100%">
            <Heading
              level="h2"
              border="bottom"
              themeOverride={H2_THEME_OVERRIDE}
            >
              {t("dashboard.goalsAndObjectives")}:
            </Heading>
            <List isUnstyled margin="small 0 0 large">
              {publicResults.goals.map((goal) => (
                <List.Item key={goal.id} margin="0">
                  <Text weight="bold">{goal.description}</Text>
                  <List>
                    {goal.objectives.map((objective) => (
                      <List.Item key={objective.id}>
                        {objective.description}
                      </List.Item>
                    ))}
                  </List>
                </List.Item>
              ))}
            </List>
          </Flex.Item>
        )}
        {sections.map((section, sectionKey) => (
          <Flex.Item key={sectionKey} size="100%">
            <Flex wrap="wrap" gap="small">
              <Flex.Item size="100%">
                <Heading
                  level="h2"
                  border="bottom"
                  themeOverride={H2_THEME_OVERRIDE}
                >
                  {section.name}
                </Heading>
              </Flex.Item>
              <Flex.Item size="100%" padding="0 0 0 large">
                <RichTextEditor
                  className="readOnlyClass"
                  readOnly
                  value={RichTextEditor.createValueFromString(
                    section.description,
                    "html"
                  )}
                />
              </Flex.Item>
              {section.measures.map((measure, measureKey) => (
                <Flex.Item key={measureKey} size="100%" padding="0 0 0 large">
                  {(measure.type === CANVAS || measure.type === EXTERNAL) && (
                    <>
                      {measure.assessment_type !== "flexible" &&
                        !measure.collecting_evidence && (
                          <PublicAssessmentChart
                            measure={measure}
                            filters={filters}
                            canvasView={canvasView}
                            jaView={jaView}
                          />
                        )}
                      {measure.type === CANVAS &&
                        measure.assessment_type === "flexible" && (
                          <PublicFlexibleAssessmentChart
                            measure={measure}
                            filters={filters}
                            canvasView={canvasView}
                            jaView={jaView}
                          />
                        )}
                      {measure.type === CANVAS &&
                        measure.collecting_evidence && (
                          <PublicCanvasJuriedAssessmentChart
                            measure={measure}
                            filters={filters}
                            canvasView={canvasView}
                            jaView={jaView}
                            tenantId={tenantId}
                          />
                        )}
                      {measure.type === EXTERNAL &&
                        measure.collecting_evidence && (
                          <PublicExternalJuriedAssessmentChart
                            measure={measure}
                            filters={filters}
                            jaView={jaView}
                            tenantId={tenantId}
                          />
                        )}
                    </>
                  )}
                  {measure.type === QUALITATIVE && (
                    <PublicQualitativeMeasure
                      measure={measure}
                      tenantId={tenantId}
                    />
                  )}
                  {measure.type === EMBEDDED && (
                    <EmbeddedDashboardResults measure={measure} publicResults />
                  )}
                </Flex.Item>
              ))}
              <Flex.Item size="100%" padding="0 0 0 large">
                <PublicTextArea
                  id={section.id}
                  label={
                    <Heading level="h3">{t("results.sectionFindings")}</Heading>
                  }
                  type="public-findings"
                  tenantId={tenantId}
                />
              </Flex.Item>
              <Flex.Item size="100%" padding="0 0 0 large">
                <PublicTextArea
                  id={section.id}
                  label={
                    <Heading level="h3">
                      {t("results.sectionRecommendations")}
                    </Heading>
                  }
                  type="public-recommendations"
                  tenantId={tenantId}
                />
              </Flex.Item>
            </Flex>
          </Flex.Item>
        ))}
      </Flex>
      <div
        ref={bottomRef}
        style={{
          borderTop: "1px dashed #ccc",
          marginBottom: 10,
          marginTop: 25,
        }}
      />
      <Card style={{ backgroundColor: "#F1F1F1", height: "auto" }}>
        <Flex wrap="wrap" gap="small">
          <Flex.Item size="100%">
            <PublicTextArea
              id={publicResults.cycle_id}
              label={<Heading level="h3">{t("results.cycleFindings")}</Heading>}
              type="public-findings"
              tenantId={tenantId}
            />
          </Flex.Item>
          <Flex.Item size="100%">
            <PublicTextArea
              id={publicResults.cycle_id}
              label={
                <Heading level="h3">
                  {t("results.cycleRecommendations")}
                </Heading>
              }
              type="public-recommendations"
              tenantId={tenantId}
            />
          </Flex.Item>
        </Flex>
      </Card>
      <Button
        size="small"
        onClick={() => topRef.current.scrollIntoView()}
        margin="medium 0 0 0"
      >
        {t("results.returnToTop")}
      </Button>
    </View>
  ) : (
    <ResultsSkeleton />
  );
};

export default PublicResults;
